/* eslint-disable @next/next/no-img-element */
import CommonApi from '@/pages/api'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import ForgotPasswordForm, {
  type Props as ForgotPasswordFormProps
} from '@/src/components/forgot-password/ForgotPasswordForm'
import Button from '@/src/components/button'
import ResetForm from '@/src/components/forgot-password/ResetForm'
import { Divider, Skeleton, Typography } from '@mui/material'
import Link from 'next/link'
import { useStore } from '@/src/types/store'
import dayjs from 'dayjs'
import AwsCaptchaComponent from '@/src/components/captcha/Captcha'
import CustomModal from '@/src/components/Modal'
import AuthBanner from '@/src/components/auth/AuthBanner'

const ForgotPassword = () => {
  const { themeLogo, themeLoading, openToast } = useStore()
  const router = useRouter()
  const [showcCaptcha, setShowCaptcha] = useState(false)
  const [loading, setloading] = useState(false)
  const [isReset, setIsReset] = useState(false)
  const [userEmail, setUserEmail] = useState('')

  const [confirmationCodeSent, setConfirmationCodeSent] = useState(false)

  const handlePassCaptcha: ForgotPasswordFormProps['onSubmit'] = values => {
    setUserEmail(values?.email)
    setShowCaptcha(true)
  }

  const handleFormValues: ForgotPasswordFormProps['onSubmit'] = async values => {
    setloading(true)
    setUserEmail(values?.email)
    try {
      const response = await CommonApi({
        path: 'forget-password',
        method: 'post',
        data: { email: values?.email }
      })
      if (response.status) {
        setConfirmationCodeSent(true)
        openToast(response.msg || 'Code sent your email successfully!', 'success')
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Something went wrong', 'error')
    } finally {
      setloading(false)
    }
  }

  const handleSendVerificationCode = async (userName?: string) => {
    try {
      const response = await CommonApi({
        path: 'forget-password',
        method: 'post',
        data: {
          email: userName ?? userEmail
        }
      })
      if (response.status) {
        openToast(response.msg || 'Code sent to your email successfully!', 'success')
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Something went wrong', 'error')
    }
  }
  const handleResetValues = async (values: any) => {
    setloading(true)
    try {
      const response = await CommonApi({
        path: 'confirm-forgot-password',
        method: 'post',
        data: {
          email: userEmail,
          new_password: values?.newPassword,
          confirmation_code: values?.confirmationCode
        }
      })
      if (response.status) {
        setIsReset(true)
        openToast(response.msg || 'Password reset successfully!', 'success')
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Password not confirmed!', 'error')
    } finally {
      setloading(false)
    }
  }

  const onCaptchaSuccess = () => {
    setShowCaptcha(false)
    handleFormValues({ email: userEmail })
  }

  return (
    <div className="flex w-full h-[100dvh] justify-between">
      <div className="w-full md:w-[50vw] h-screen overflow-hidden max-md:overflow-y-auto relative flex flex-col pt-8 pb-16 md:pb-0 md:mt-0 md:justify-center items-center ">
        <div className="w-full flex flex-col items-center my-auto md:my-0">
          <div className="w-full flex justify-center">
            {themeLoading ? (
              <Skeleton className="h-[4.75rem] w-[10rem]" />
            ) : (
              <img
                src={themeLogo.logo}
                alt="Logo"
                className=" max-w-[20.75rem] h-[2.75rem] object-contain"
              />
            )}
          </div>
          <Typography variant="h1" className="mb-8 mt-8 md:mt-16 text-center">
            {!isReset ? 'Reset Password' : 'Your password has been reset'}
          </Typography>
          {isReset ? (
            <Button
              text="Sign in"
              onClick={() => router.push('/login')}
              divClass="w-[20.438rem] sm:w-[26.5rem]"
              fullwidth={true}
              extraClass="!normal-case"
            />
          ) : confirmationCodeSent ? (
            <ResetForm
              reSendCode={handleSendVerificationCode}
              loading={loading}
              onSubmit={handleResetValues}
              userEmail={userEmail}
            />
          ) : (
            <ForgotPasswordForm loading={loading} onSubmit={handlePassCaptcha} />
          )}
        </div>

        <div className="w-full pl-4 flex justify-start gap-4 absolute bottom-4 left-0 md:left-8 ">
          <Typography variant="caption" color="text.secondary">
            © {dayjs().format('YYYY')} Relait PTY LTD
          </Typography>{' '}
          <div className="flex items-center gap-2">
            <Typography variant="buttonLarge_sm" color="text.secondary">
              <a
                href="https://www.relait.com.au/privacy-policy"
                target="_blank"
                className="hover:underline"
              >
                Privacy
              </a>
            </Typography>
            <Divider orientation="vertical" className="h-3 w-px items-center flex justify-center" />
            <Typography variant="buttonLarge_sm" color="text.secondary">
              <Link
                href="https://www.relait.com.au/eula"
                target="_blank"
                className="hover:underline"
              >
                Terms
              </Link>
            </Typography>
          </div>
        </div>

        <CustomModal
          crossIcon={true}
          classes=" w-[37.5rem] h-fit min-h-[34.375rem] p-4"
          open={showcCaptcha}
          handleClose={() => setShowCaptcha(false)}
        >
          {showcCaptcha && <AwsCaptchaComponent onCaptchaSuccess={onCaptchaSuccess} />}
        </CustomModal>
      </div>
      <div className="hidden md:flex justify-end w-[50vw] h-full">
        <AuthBanner />
      </div>
    </div>
  )
}
export default ForgotPassword
